/**
 * Fixed-distance celestial impostors (spec §4.4; ponytail: M3 replaces them with a scaled-space pass): the star
 * (E4 disc, bounded glare sprite and one anamorphic streak, style guide §3.6-§3.8, §9.6) and the banded gas giant
 * (flow-warped TSL bands with storms, a key-light terminator, an analytic atmosphere and an optional thin ring).
 * Asset-lane owned (task A8). Pure maths is exported and tested; per-frame code is allocation-free.
 */
import {
  AdditiveBlending,
  Color,
  DoubleSide,
  BackSide,
  Group,
  Mesh,
  MeshBasicNodeMaterial,
  PlaneGeometry,
  RingGeometry,
  SphereGeometry,
  Vector3,
  type Node,
  type Object3D,
  type UniformNode,
} from 'three/webgpu'
import {
  abs,
  cameraPosition,
  cameraProjectionMatrix,
  cos,
  cross,
  dot,
  exp,
  float,
  length,
  max,
  mix,
  modelPosition,
  modelViewMatrix,
  mx_fractal_noise_float,
  mx_fractal_noise_vec3,
  normalize,
  normalWorld,
  positionGeometry,
  positionLocal,
  positionWorld,
  pow,
  saturate,
  select,
  sin,
  smoothstep,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
  vec4,
  viewportSize,
} from 'three/tsl'
import { createRng, range } from '../../core/rng'
import { unitDirection, type EnvironmentFile, type Vec3Tuple } from './data'

const DEG = Math.PI / 180
const INV_PI = 1 / Math.PI

type FloatNode = Node<'float'>
type Vec2Node = Node<'vec2'>
type Vec3Node = Node<'vec3'>
type Vec4Node = Node<'vec4'>
type FloatUniform = UniformNode<'float', number>

// ---------------------------------------------------------------------------------------------------------------
// Pure impostor maths (tested)
// ---------------------------------------------------------------------------------------------------------------

/** World radius of a sphere at `distance` that subtends `angularRadiusDeg`. */
export function impostorRadius(distance: number, angularRadiusDeg: number): number {
  return distance * Math.tan(angularRadiusDeg * DEG)
}

/** Places `obj` at `distance` along `direction` (normalized here). Allocation-free. */
export function placeImpostor(obj: Object3D, direction: Vec3Tuple, distance: number): void {
  const l = Math.hypot(direction[0], direction[1], direction[2])
  const k = distance / l
  obj.position.set(direction[0] * k, direction[1] * k, direction[2] * k)
}

/** Angular radius (degrees) the impostor subtends: the inverse of impostorRadius. */
export function impostorAngularRadiusDeg(distance: number, radius: number): number {
  return Math.atan(radius / distance) / DEG
}

/** On-screen radius in pixels of an angular radius for a vertical FOV and viewport height. */
export function angularRadiusToPixels(angularRadiusDeg: number, fovYDeg: number, heightPx: number): number {
  return (Math.tan(angularRadiusDeg * DEG) / Math.tan((fovYDeg * DEG) / 2)) * (heightPx / 2)
}

/** Glare sprite half-extent in NDC for a radius given as a fraction of the screen height. */
export function glareHalfExtentNdc(maxRadius: number, aspect: number): { x: number; y: number } {
  return { x: (2 * maxRadius) / aspect, y: 2 * maxRadius }
}

/** Streak half-extent in NDC: `length` is the total length as a fraction of the screen width. */
export function streakHalfExtentNdc(lengthFraction: number, thicknessFraction: number): { x: number; y: number } {
  return { x: lengthFraction, y: 2 * thicknessFraction }
}

/** On-screen streak length in pixels (total, both sides). */
export function streakLengthPx(lengthFraction: number, widthPx: number): number {
  return streakHalfExtentNdc(lengthFraction, 0.01).x * widthPx
}

/** Linear radiance of a Lambert surface of `albedo` lit head-on by a directional light of `intensity`. */
export function lambertRadiance(albedo: number, intensity: number): number {
  return albedo * intensity * INV_PI
}

// ---------------------------------------------------------------------------------------------------------------
// Screen-space helpers
// ---------------------------------------------------------------------------------------------------------------

/** Clip position of the object's origin (the impostor centre). */
function centreClip(): Vec4Node {
  return cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(0, 0, 0, 1)))
}

/**
 * A unit quad (PlaneGeometry 1 × 1) expanded in NDC around the impostor centre, keeping the centre's depth so
 * ships in front occlude it per pixel (style guide §9.6). `half` is the NDC half-extent.
 */
function screenQuad(half: Vec2Node): Vec4Node {
  const c = centreClip()
  const offset = positionGeometry.xy.mul(2).mul(half).mul(c.w)
  return vec4(c.xy.add(offset), c.z, c.w)
}

/** 1 while the impostor centre is on screen and in front, fading to 0 just past the edges. */
function onScreen(): FloatNode {
  const c = centreClip()
  const ndc = c.xy.div(max(c.w, 1e-6))
  const edge = max(abs(ndc.x), abs(ndc.y))
  return select(c.w.greaterThan(0), float(1).sub(smoothstep(1.0, 1.2, edge)), float(0))
}

// ---------------------------------------------------------------------------------------------------------------
// Star
// ---------------------------------------------------------------------------------------------------------------

export interface StarImpostor {
  readonly root: Group
  readonly uniforms: {
    intensity: FloatUniform
    glare: FloatUniform
    streak: FloatUniform
  }
  dispose(): void
}

export function createStar(env: EnvironmentFile): StarImpostor {
  const s = env.star
  const quad = new PlaneGeometry(1, 1)
  const root = new Group()
  root.name = 'star'
  placeImpostor(root, env.lights.key.direction, env.impostorDistance)
  const uIntensity = uniform(s.intensity)
  const uGlare = uniform(s.glare.intensity)
  const uStreak = uniform(s.streak.intensity)

  // E4 disc: a camera-facing billboard at the true angular size, limb-darkened, anti-aliased edge.
  const radius = impostorRadius(env.impostorDistance, s.angularRadiusDeg)
  const disc = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  const viewCentre = modelViewMatrix.mul(vec4(0, 0, 0, 1))
  disc.vertexNode = cameraProjectionMatrix.mul(vec4(viewCentre.xyz.add(vec3(positionGeometry.xy.mul(radius * 2.4), 0)), 1))
  const r = length(uv().sub(0.5)).mul(2.4)
  const mu = saturate(float(1).sub(r.mul(r))).sqrt()
  const limb = float(1).sub(float(s.limbDarkening).mul(float(1).sub(mu)))
  const edge = float(1).sub(smoothstep(0.96, 1.04, r))
  disc.colorNode = rgb(s.color).mul(uIntensity).mul(limb).mul(edge)
  const discMesh = new Mesh(quad, disc)
  discMesh.name = 'star:disc'
  discMesh.frustumCulled = false

  // Glare: bounded radius (fraction of screen height), aspect-correct circle, fades as the star leaves the view.
  const glare = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  const aspect = viewportSize.x.div(viewportSize.y)
  glare.vertexNode = screenQuad(vec2(float(2 * s.glare.maxRadius).div(aspect), 2 * s.glare.maxRadius))
  const vis = varying(onScreen(), 'vStarVisible')
  const d = length(uv().sub(0.5)).mul(2)
  // Lens glare falls off like 1/r² from a core of width `core`, windowed to zero at the maximum radius.
  const w2 = s.glare.core * s.glare.core
  const falloff = float(w2).div(d.mul(d).add(w2))
  const win = pow(saturate(float(1).sub(d)), 2)
  glare.colorNode = rgb(s.glare.color).mul(uGlare).mul(falloff).mul(win).mul(vis)
  const glareMesh = new Mesh(quad, glare)
  glareMesh.name = 'star:glare'
  glareMesh.frustumCulled = false

  // One anamorphic streak, total length <= 25% of the screen width (style guide §3.8).
  const streak = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  const sh = streakHalfExtentNdc(s.streak.length, s.streak.thickness)
  streak.vertexNode = screenQuad(vec2(sh.x, sh.y))
  const sx = abs(uv().x.sub(0.5)).mul(2)
  const sy = abs(uv().y.sub(0.5)).mul(2)
  const along = exp(sx.mul(-2.2)).mul(pow(float(1).sub(sx), 1.5))
  const across = exp(sy.mul(sy).mul(-14)).mul(float(1).sub(sy))
  streak.colorNode = rgb(s.streak.color).mul(uStreak).mul(along).mul(across).mul(varying(onScreen(), 'vStreakVisible'))
  const streakMesh = new Mesh(quad, streak)
  streakMesh.name = 'star:streak'
  streakMesh.frustumCulled = false

  // Draw after the sky and the giant, before ships' transparent effects.
  discMesh.renderOrder = 1
  glareMesh.renderOrder = 2
  streakMesh.renderOrder = 2
  root.add(discMesh, glareMesh, streakMesh)
  return {
    root,
    uniforms: { intensity: uIntensity, glare: uGlare, streak: uStreak },
    dispose(): void {
      quad.dispose()
      disc.dispose()
      glare.dispose()
      streak.dispose()
    },
  }
}

/** A constant linear RGB node from an sRGB hex colour. */
function rgb(hex: string): Vec3Node {
  const c = new Color(hex)
  return vec3(c.r, c.g, c.b)
}

// ---------------------------------------------------------------------------------------------------------------
// Gas giant
// ---------------------------------------------------------------------------------------------------------------

/** Chord length on the unit sphere for an angular radius. */
export function chordForAngle(angleDeg: number): number {
  return 2 * Math.sin((angleDeg * DEG) / 2)
}

/** Unit vector at planetographic latitude and longitude (degrees), +Y the spin axis. */
export function latLonToUnit(latDeg: number, lonDeg: number): [number, number, number] {
  const la = latDeg * DEG
  const lo = lonDeg * DEG
  return [Math.cos(la) * Math.cos(lo), Math.sin(la), Math.cos(la) * Math.sin(lo)]
}

/** Rodrigues rotation of `p` about the unit axis `c` by an angle that falls off with distance from `c`. */
function swirl(p: Vec3Node, c: Vec3Tuple, chord: number, twist: number): Vec3Node {
  const axis = vec3(c[0], c[1], c[2])
  const d = p.sub(axis)
  const theta = exp(dot(d, d).div(-(chord * chord))).mul(twist)
  const ct = cos(theta)
  const st = sin(theta)
  return p.mul(ct).add(cross(axis, p).mul(st)).add(axis.mul(dot(axis, p).mul(float(1).sub(ct))))
}

/** Ring density 0..1 at a radius given in planet radii (ringlets, a Cassini-like gap, soft edges). */
function ringDensity(rr: FloatNode, inner: number, outer: number, seed: number): FloatNode {
  const t = rr.sub(inner).div(outer - inner)
  // Low radial frequencies only: the ring is a few pixels tall on screen, so fine ringlets would alias.
  const ringlets = mx_fractal_noise_float(vec3(t.mul(14), seed, 0.5), 3, 2.0, 0.5).mul(0.5).add(0.5)
  const gap = float(1).sub(smoothstep(0.03, 0.0, abs(t.sub(0.64))).mul(0.8))
  const edges = smoothstep(0, 0.08, t).mul(float(1).sub(smoothstep(0.88, 1, t)))
  return saturate(ringlets.mul(1.1).sub(0.05)).mul(gap).mul(edges).mul(float(0.5).add(t.mul(0.5)))
}

/**
 * Spin axis of the giant, framed for the view from the render origin: start from screen-up at the giant, roll it
 * in the screen plane by `rollDeg`, then lean it toward the viewer by `openDeg` (0 = ring edge-on).
 */
export function giantAxis(toGiant: Vec3Tuple, rollDeg: number, openDeg: number): [number, number, number] {
  const v = unitDirection([-toGiant[0], -toGiant[1], -toGiant[2]])
  const upDot = v[1]
  const u0 = unitDirection([-upDot * v[0], 1 - upDot * v[1], -upDot * v[2]])
  // Screen-right at the giant as seen by the viewer (looking along -v): r = u0 x v.
  const r0: [number, number, number] = [u0[1] * v[2] - u0[2] * v[1], u0[2] * v[0] - u0[0] * v[2], u0[0] * v[1] - u0[1] * v[0]]
  const ro = rollDeg * DEG
  const op = openDeg * DEG
  const ur = [u0[0] * Math.cos(ro) + r0[0] * Math.sin(ro), u0[1] * Math.cos(ro) + r0[1] * Math.sin(ro), u0[2] * Math.cos(ro) + r0[2] * Math.sin(ro)]
  return unitDirection([ur[0] * Math.cos(op) + v[0] * Math.sin(op), ur[1] * Math.cos(op) + v[1] * Math.sin(op), ur[2] * Math.cos(op) + v[2] * Math.sin(op)])
}

function orientGiant(root: Object3D, toGiant: Vec3Tuple, rollDeg: number, openDeg: number): void {
  const a = giantAxis(toGiant, rollDeg, openDeg)
  root.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), new Vector3(a[0], a[1], a[2]))
}

/** Longitude (degrees, giant frame) of the meridian facing the render origin. */
export function facingLongitudeDeg(root: Object3D): number {
  const v = root.position.clone().negate().applyQuaternion(root.quaternion.clone().invert())
  return Math.atan2(v.z, v.x) / DEG
}

export interface GasGiantImpostor {
  readonly root: Group
  readonly uniforms: { spin: FloatUniform; atmosphere: FloatUniform }
  /** Radians per second the bands flow (rigid spin of the pattern). */
  readonly flowSpeed: number
  dispose(): void
}

export function createGasGiant(env: EnvironmentFile, seed: number): GasGiantImpostor {
  const g = env.gasGiant
  const R = impostorRadius(env.impostorDistance, g.angularRadiusDeg)
  const rng = createRng((seed ^ 0x9e3779b9) >>> 0)
  const o1 = vec3(range(rng, 0, 50), range(rng, 0, 50), range(rng, 0, 50))
  const o2 = vec3(range(rng, 0, 50), range(rng, 0, 50), range(rng, 0, 50))
  const o3 = vec3(range(rng, 0, 50), range(rng, 0, 50), range(rng, 0, 50))
  const ringSeed = range(rng, 0, 50)

  const root = new Group()
  root.name = 'gasGiant'
  placeImpostor(root, env.lights.fill.direction, env.impostorDistance)
  orientGiant(root, unitDirection(env.lights.fill.direction), g.axisRollDeg, g.axisOpenDeg)
  root.updateMatrixWorld(true)
  const facing = facingLongitudeDeg(root)

  // Key light direction in the giant's local frame (the frame is fixed; the pattern spins in the shader).
  const keyWorld = unitDirection(env.lights.key.direction)
  const keyLocalV = new Vector3(keyWorld[0], keyWorld[1], keyWorld[2]).applyQuaternion(root.quaternion.clone().invert()).normalize()
  const keyLocal = vec3(keyLocalV.x, keyLocalV.y, keyLocalV.z)
  const keyW = vec3(keyWorld[0], keyWorld[1], keyWorld[2])
  const keyCol = new Color(env.lights.key.color).multiplyScalar(env.lights.key.intensity * INV_PI)
  const uSpin = uniform(0)
  const uAtmo = uniform(g.atmosphere.intensity)
  const pal = g.palette.map((h) => rgb(h))
  const inner = g.ring.inner
  const outer = g.ring.outer

  // --- Surface ---
  const nL = normalize(positionLocal)
  const cs = cos(uSpin)
  const sn = sin(uSpin)
  const p = vec3(nL.x.mul(cs).sub(nL.z.mul(sn)), nL.y, nL.x.mul(sn).add(nL.z.mul(cs)))
  // Flow warp: eddies stretched along the bands (longitude), then storms twist the warped domain.
  const w = mx_fractal_noise_vec3(p.mul(vec3(1.6, 5.5, 1.6)).add(o1), 4, 2.0, 0.55)
  let pw: Vec3Node = p.add(w.mul(g.bands.warp))
  let stormMask: FloatNode = float(0)
  let stormColour: Vec3Node = vec3(0, 0, 0)
  for (const st of g.storms) {
    const c = latLonToUnit(st.latDeg, facing + st.lonDeg)
    const chord = chordForAngle(st.radiusDeg)
    pw = swirl(pw, c, chord, st.twist)
    const dc = p.sub(vec3(c[0], c[1], c[2]))
    // Oval: storms are wider in longitude than in latitude.
    const m = exp(dot(dc.mul(vec3(1, 1.8, 1)), dc.mul(vec3(1, 1.8, 1))).div(-(chord * chord * 0.3))).mul(st.tint)
    stormColour = stormColour.add(rgb(st.color).mul(m))
    stormMask = stormMask.add(m)
  }
  const freq = g.bands.frequency * Math.PI
  // Band edges curl into eddies: displace latitude most where the band pattern crosses zero.
  const edge = float(1).sub(smoothstep(0.0, 0.55, abs(sin(pw.y.mul(freq)))))
  const curl = mx_fractal_noise_float(pw.mul(vec3(7, 7, 7)).add(o2), 3, 2.0, 0.5).mul(edge).mul(g.bands.edgeCurl)
  const streaks = mx_fractal_noise_float(pw.mul(vec3(2.5, 40, 2.5)).add(o3), 3, 2.0, 0.5)
  const lat = pw.y.add(curl).add(streaks.mul(g.bands.streaks))
  const turb = mx_fractal_noise_float(pw.mul(vec3(5, 18, 5)).add(o1), 4, 2.0, 0.5)
  const s1 = sin(lat.mul(freq))
  const s2 = sin(lat.mul(freq * 0.37).add(1.3)).mul(0.5).add(0.5)
  const s3 = sin(lat.mul(freq * 0.61).add(0.4)).mul(0.5).add(0.5)
  const zone = smoothstep(-0.3, 0.3, s1.add(turb.mul(g.bands.turbulence)))
  const belt = mix(mix(pal[0], pal[1], s2), pal[2], smoothstep(0.55, 0.9, s3).mul(0.8))
  const light = mix(pal[3], pal[4], smoothstep(0.3, 0.8, s2.add(turb.mul(0.2))))
  const fineStreaks = mx_fractal_noise_float(pw.mul(vec3(3, 110, 3)).add(o2), 2, 2.0, 0.5)
  let albedo: Vec3Node = mix(belt, light, zone).mul(float(0.94).add(turb.mul(0.12)).add(fineStreaks.mul(0.1)))
  // Polar regions: darker, less banded.
  albedo = mix(albedo, pal[0].mul(0.85), smoothstep(0.75, 0.97, abs(p.y)))
  albedo = mix(albedo, stormColour.div(max(stormMask, 1e-4)), saturate(stormMask)).mul(g.albedo)

  // Lighting: soft terminator with a warm scattering band, limb darkening on the lit side.
  const nW = normalize(normalWorld)
  const V = normalize(cameraPosition.sub(positionWorld))
  const NdotV = saturate(dot(nW, V))
  const NdotL = dot(nL, keyLocal)
  const wrap = g.terminator.wrap
  const diffuse = pow(saturate(NdotL.add(wrap).div(1 + wrap)), 1.15)
  const warm = float(1).sub(smoothstep(0, g.terminator.width, NdotL)).mul(smoothstep(-wrap, 0.02, NdotL))
  const tint = mix(vec3(1, 1, 1), rgb(g.terminator.color), warm.mul(0.7))
  const limbDark = mix(float(0.72), float(1), pow(NdotV, 0.35))
  // Ring shadow on the bands: the ray toward the star crosses the ring plane (local y = 0).
  let ringShadow: FloatNode = float(1)
  if (g.ring.enabled) {
    const t = positionLocal.y.negate().div(keyLocalV.y)
    const hit = positionLocal.add(keyLocal.mul(t))
    const rr = length(vec2(hit.x, hit.z)).div(R)
    const inside = t.greaterThan(0).and(rr.greaterThan(inner)).and(rr.lessThan(outer))
    ringShadow = select(inside, float(1).sub(ringDensity(rr, inner, outer, ringSeed).mul(g.ring.opacity * 0.9)), float(1))
  }
  const lit = albedo.mul(vec3(keyCol.r, keyCol.g, keyCol.b)).mul(diffuse).mul(tint).mul(limbDark).mul(ringShadow)
  // Night side: faint nebula light so the disc reads as a dark silhouette, never a hole.
  const night = albedo.mul(rgb(g.night.color)).mul(g.night.intensity).mul(float(1).sub(diffuse))
  const fres = pow(float(1).sub(NdotV), g.atmosphere.rimPower)
  const atmoLit = smoothstep(-0.25, 0.35, NdotL)
  const rim = rgb(g.atmosphere.color).mul(uAtmo).mul(fres).mul(atmoLit).mul(0.6)
  const surface = new MeshBasicNodeMaterial()
  surface.colorNode = lit.add(night).add(rim)
  const planet = new Mesh(new SphereGeometry(R, 160, 96), surface)
  planet.name = 'gasGiant:surface'
  planet.frustumCulled = false

  // --- Atmosphere halo: analytic closest-approach density on a back-facing shell ---
  const Rs = R * (1 + g.atmosphere.thickness)
  const shell = new MeshBasicNodeMaterial({ side: BackSide, transparent: true, depthWrite: false, blending: AdditiveBlending })
  const dir = normalize(positionWorld.sub(cameraPosition))
  const toC = modelPosition.sub(cameraPosition)
  const q = cameraPosition.add(dir.mul(dot(toC, dir))).sub(modelPosition)
  const h = length(q)
  const x = saturate(h.sub(R).div(Rs - R))
  const density = exp(x.mul(-g.atmosphere.falloff)).mul(float(1).sub(x))
  const haloLit = smoothstep(-0.3, 0.3, dot(q.div(h), keyW))
  shell.colorNode = rgb(g.atmosphere.color).mul(uAtmo).mul(density).mul(haloLit)
  const halo = new Mesh(new SphereGeometry(Rs, 128, 64), shell)
  halo.name = 'gasGiant:atmosphere'
  halo.frustumCulled = false
  halo.renderOrder = 1

  root.add(planet, halo)

  // --- Thin ring with the planet's shadow ---
  let ringMesh: Mesh | null = null
  if (g.ring.enabled) {
    const ringMat = new MeshBasicNodeMaterial({ side: DoubleSide, transparent: true, depthWrite: false })
    const P = positionLocal
    const rr = length(vec2(P.x, P.z)).div(R)
    const dens = ringDensity(rr, inner, outer, ringSeed)
    const bb = dot(P, keyLocal)
    const disc = bb.mul(bb).sub(dot(P, P)).add(R * R).div(R * R)
    const inShadow = smoothstep(-0.004, 0.004, disc).mul(select(bb.lessThan(0), float(1), float(0)))
    const cosL = abs(keyLocal.y)
    const ringLight = vec3(keyCol.r, keyCol.g, keyCol.b).mul(float(0.6).add(cosL.mul(0.4)))
    ringMat.colorNode = rgb(g.ring.color).mul(ringLight).mul(float(1).sub(inShadow.mul(0.92)))
    ringMat.opacityNode = dens.mul(g.ring.opacity)
    const ringGeo = new RingGeometry(R * inner, R * outer, 256, 4)
    ringGeo.rotateX(-Math.PI / 2)
    ringMesh = new Mesh(ringGeo, ringMat)
    ringMesh.name = 'gasGiant:ring'
    ringMesh.frustumCulled = false
    ringMesh.renderOrder = 2
    root.add(ringMesh)
  }

  return {
    root,
    uniforms: { spin: uSpin, atmosphere: uAtmo },
    flowSpeed: g.flow.speed,
    dispose(): void {
      planet.geometry.dispose()
      surface.dispose()
      halo.geometry.dispose()
      shell.dispose()
      if (ringMesh) {
        ringMesh.geometry.dispose()
        ;(ringMesh.material as MeshBasicNodeMaterial).dispose()
      }
    },
  }
}
